const API_URL = window.location.origin;
const AUTH_TOKEN_KEY = "tv_auth_token";
const AUTH_USER_KEY = "tv_auth_user";

// Keycloak SSO session tracking (TSP-129)
const KC_AUTH_KEY = "kc_auth";
const KC_SESSION_KEY = "kc_session_state";
const KC_ID_TOKEN_KEY = "kc_id_token";

let _kcConfig = null;
export async function getKeycloakConfig() {
  if (_kcConfig) return _kcConfig;
  const res = await fetch(`${API_URL}/auth/keycloak/config`);
  if (!res.ok) return null;
  _kcConfig = await res.json();
  return _kcConfig;
}

export function getKeycloakRedirectUri() {
  // Use a clean URL without hash - Keycloak appends ?code=xxx to the redirect URI
  return window.location.origin + window.location.pathname;
}

export async function keycloakLogin(returnUrl) {
  const config = await getKeycloakConfig();
  if (!config) return;
  sessionStorage.setItem("kc_login_pending", "1");
  if (returnUrl) {
    sessionStorage.setItem("kc_return_url", returnUrl);
  }
  const redirectUri = getKeycloakRedirectUri();
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid",
  });
  // OAuth state param carries the returnUrl through the KC round trip. KC
  // echoes it back verbatim on the callback, so it survives even if
  // sessionStorage is cleared (e.g., by Safari ITP on cross-site redirect).
  if (returnUrl) {
    params.set("state", encodeURIComponent(returnUrl));
  }
  window.location.href = `${config.authUrl}?${params.toString()}`;
}

export async function handleKeycloakCallback(code) {
  const redirectUri = getKeycloakRedirectUri();
  const res = await fetch(`${API_URL}/auth/keycloak`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, redirectUri }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Keycloak login failed");
  }
  const data = await res.json();
  localStorage.setItem(AUTH_TOKEN_KEY, data.token);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
  // Store KC session info for session checks and proper logout
  localStorage.setItem(KC_AUTH_KEY, "1");
  if (data.kcSessionState) {
    localStorage.setItem(KC_SESSION_KEY, data.kcSessionState);
  }
  if (data.kcIdToken) {
    localStorage.setItem(KC_ID_TOKEN_KEY, data.kcIdToken);
    // Mirror to sessionStorage so cross-tab logout handlers can still read
    // the id_token after the originating tab clears localStorage. Without
    // this, KC would show its "You are logged out" intermediate page in
    // the other tabs (id_token_hint is required for silent logout).
    sessionStorage.setItem(KC_ID_TOKEN_KEY, data.kcIdToken);
  }
  return data;
}

// Mirrors KC_ID_TOKEN_KEY from localStorage into the current tab's
// sessionStorage if not already there. Called from App.js on mount so a
// freshly-opened tab (e.g. one launched from the Etendo camera icon)
// inherits the id_token it'll need at logout time.
export function syncKcIdTokenToSession() {
  if (sessionStorage.getItem(KC_ID_TOKEN_KEY)) return;
  const idToken = localStorage.getItem(KC_ID_TOKEN_KEY);
  if (idToken) sessionStorage.setItem(KC_ID_TOKEN_KEY, idToken);
}

// Reads the id_token preferring localStorage, falling back to sessionStorage.
// Cross-tab logout handlers run AFTER the originating tab has cleared
// localStorage, so they need the sessionStorage fallback to send id_token_hint
// to KC and skip the intermediate "You are logged out" page.
export function getKcIdToken() {
  return localStorage.getItem(KC_ID_TOKEN_KEY) || sessionStorage.getItem(KC_ID_TOKEN_KEY);
}

export async function keycloakLogout() {
  const config = await getKeycloakConfig();
  // Prefer localStorage (single-tab logout) but fall back to sessionStorage
  // (cross-tab tabs that already had localStorage cleared by the originator).
  const idToken = getKcIdToken();
  clearLocalSession();
  sessionStorage.removeItem(KC_ID_TOKEN_KEY);
  if (config && config.etendoLogoutUrl) {
    // Route through Etendo's LoginHandler so users see the back-office goodbye
    // page (shared-device warning + "here" link to finalize KC logout). KC's
    // SSO session is terminated when they click "here" - this matches the
    // back-office logout flow exactly per TSP-129.
    // id_token and client_id are forwarded so the "here" link terminates the
    // TankView KC session (not the Etendo one) and KC renders the TankView
    // branded logout page.
    const params = new URLSearchParams({
      logout: "true",
      client_id: config.clientId,
      post_logout_redirect_uri: window.location.origin + window.location.pathname,
    });
    if (idToken) {
      params.set("id_token", idToken);
    }
    window.location.href = `${config.etendoLogoutUrl}?${params.toString()}`;
    return;
  }
  if (config) {
    const params = new URLSearchParams({ client_id: config.clientId });
    if (idToken) {
      params.set("id_token_hint", idToken);
    }
    window.location.href = `${config.logoutUrl}?${params.toString()}`;
  }
}

// Check if Keycloak session is still active using KC's check_session_iframe.
// Returns true if session is active or if check cannot be performed.
// Returns false if KC session has been terminated (user logged out elsewhere).
export async function checkKeycloakSession() {
  if (!isKeycloakAuth()) return true;

  const config = await getKeycloakConfig();
  const sessionState = localStorage.getItem(KC_SESSION_KEY);
  if (!config || !sessionState) return true;

  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = config.realmUrl + "/protocol/openid-connect/login-status-iframe.html";

    const timeout = setTimeout(() => {
      cleanup();
      resolve(true); // timeout - assume OK to avoid false negatives
    }, 5000);

    const onMessage = (event) => {
      // Only handle messages from our iframe
      if (event.source !== iframe.contentWindow) return;
      cleanup();
      if (event.data === "unchanged") {
        resolve(true);
      } else {
        // "changed" or "error" - session has ended
        resolve(false);
      }
    };

    const cleanup = () => {
      clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      if (iframe.parentNode) document.body.removeChild(iframe);
    };

    window.addEventListener("message", onMessage);

    iframe.onload = () => {
      // Post clientId + sessionState to the KC iframe for session check
      // postMessage origin must be just the origin (scheme + host), not the full realm URL
      const kcOrigin = new URL(config.realmUrl).origin;
      try {
        iframe.contentWindow.postMessage(
          config.clientId + " " + sessionState,
          kcOrigin
        );
      } catch (e) {
        cleanup();
        resolve(true); // cross-origin error, assume OK
      }
    };

    iframe.onerror = () => {
      cleanup();
      resolve(true); // iframe failed to load, assume OK
    };

    document.body.appendChild(iframe);
  });
}

export function isKeycloakAuth() {
  return localStorage.getItem(KC_AUTH_KEY) === "1";
}

// Clear all local auth state without redirecting
function clearLocalSession() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(KC_AUTH_KEY);
  localStorage.removeItem(KC_SESSION_KEY);
  localStorage.removeItem(KC_ID_TOKEN_KEY);
  // Note: KC_ID_TOKEN_KEY is also kept in sessionStorage (per tab) and is
  // cleared in keycloakLogout() once we've used it to build the KC URL.
}

export const login = async (username, password) => {
  const res = await fetch(`${API_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Login failed");
  }
  const data = await res.json();
  localStorage.setItem(AUTH_TOKEN_KEY, data.token);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
  return data;
};

export const logout = () => {
  if (isKeycloakAuth()) {
    keycloakLogout();
    return;
  }
  clearLocalSession();
  window.location.hash = "#/login";
  window.location.reload();
};

export const getToken = () => localStorage.getItem(AUTH_TOKEN_KEY);

export const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem(AUTH_USER_KEY));
  } catch (e) {
    return null;
  }
};

export const isAuthenticated = () => !!getToken();

export async function ssoLogin(params) {
  // Accepts { sessionId } or { username } for SSO
  const body = typeof params === "string" ? { sessionId: params } : params;
  const res = await fetch(`${API_URL}/auth/sso`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) return false;
  const data = await res.json();
  localStorage.setItem(AUTH_TOKEN_KEY, data.token);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
  return true;
}
